/**
 * New-note composer for the docked patient panel.
 *
 * Assignee options come exclusively from the facility directory API. Omitting
 * an assignee intentionally delegates creator-default assignment to the server.
 */

import { useState } from "react";
import type {
  CreateNoteRequest,
  NoteAssigneeResponse,
} from "../../../api/clinicalTrialsApiTypes";
import type { RichTextDocument } from "../../../types/notes";
import {
  createEmptyRichText,
  richTextToPlainText,
} from "../../../utils/richText";
import { RichTextEditor } from "./RichTextEditor";

interface NoteComposerProps {
  assignees: NoteAssigneeResponse[];
  sourceDocumentId?: string;
  isSaving: boolean;
  onSave: (request: CreateNoteRequest) => Promise<void>;
  onCancel: () => void;
}

/**
 * Render required title/body inputs and optional assignment metadata.
 *
 * @param props - Directory, source identity, save state, and callbacks
 * @returns New note form
 */
export function NoteComposer({
  assignees,
  sourceDocumentId,
  isSaving,
  onSave,
  onCancel,
}: NoteComposerProps) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState<RichTextDocument>(createEmptyRichText);
  const [assigneeId, setAssigneeId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const isValid = Boolean(title.trim() && richTextToPlainText(body).trim());

  return (
    <form
      className="rounded-lg border border-blue-300 bg-white shadow-sm ring-2 ring-blue-100"
      onSubmit={async (event) => {
        event.preventDefault();
        if (!isValid) return;
        await onSave({
          title: title.trim(),
          body,
          assignee_id: assigneeId || undefined,
          due_date: dueDate || undefined,
          source_document_id: sourceDocumentId,
        });
      }}
    >
      <div className="flex gap-2 border-b bg-uiGray-150 p-2">
        <select
          aria-label="Note assignee"
          value={assigneeId}
          onChange={(event) => setAssigneeId(event.target.value)}
          className="min-w-0 flex-1 rounded border border-uiGray-300 bg-white px-2 py-1 text-xs"
        >
          <option value="">Assign to me</option>
          {assignees.map((member) => (
            <option key={member.user_id} value={member.user_id}>{member.display_name || member.email}</option>
          ))}
        </select>
        <input
          type="date"
          aria-label="Note due date"
          value={dueDate}
          onChange={(event) => setDueDate(event.target.value)}
          className="w-32 rounded border border-uiGray-300 px-2 py-1 text-xs"
        />
      </div>
      <div className="p-3">
        <input
          aria-label="Note title"
          placeholder="Title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          className="mb-2 w-full border-b border-uiGray-200 pb-2 text-sm font-semibold outline-none"
          autoFocus
        />
        <RichTextEditor value={body} onChange={setBody} placeholder="Write your note…" ariaLabel="Note body" />
      </div>
      <div className="flex justify-end gap-2 border-t bg-uiGray-150 p-2">
        <button type="button" onClick={onCancel} className="btn btn-outline px-3 py-1 text-xs">Cancel</button>
        <button type="submit" disabled={!isValid || isSaving} className="btn bg-statusBlue-text px-3 py-1 text-xs text-white disabled:opacity-50">
          Save note
        </button>
      </div>
    </form>
  );
}
